import { ScrollView } from 'react-native';
import { fetchTemplates } from '@/api/services/templates';
import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import { Icon } from '@/components/Icon';
import { BodyWrapper } from '@/components/ui/therapist/BodyWrapper';
import {
  DifficultyQuery,
  Template as TemplateType,
} from '@/components/Template/types';
import { Pagination } from '@/components/ui/therapist/Pagination';
import Card from '@/components/ui/therapist/Card';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { StackScreenProps } from '@react-navigation/stack';
import { FC, useEffect, useRef, useState } from 'react';
import { View } from 'react-native-animatable';
import { Button } from '@/components/ui/therapist/Button';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import { Text } from '@/components/ui/therapist/Text';
import colors from '@/config/colors';
import { styles } from './styles';
import { logError } from '@/utils/logHandling';
import { Template } from '@/components/Template';
import { TemplateFilter } from '@/screens/TemplateList/TemplateFilter';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { IS_ANDROID } from '@/utils/platform';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const TemplatesSelect: FC<
  StackScreenProps<TherapistStackParamList, 'TemplateSelect'>
> = ({ route, navigation }) => {
  const scrollRef = useRef<ScrollView>(null);
  const [templates, setTemplates] = useState<TemplateType[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType | null>(
    null,
  );

  const [appliedFilters, setAppliedFilters] = useState<{
    difficulty?: DifficultyQuery | undefined;
    searchTerm?: string;
  }>({});

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [difficultyFilter, setDifficultyFilter] = useState<
    DifficultyQuery | undefined
  >(undefined);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [countAppliedFilters, setCountAppliedFilters] = useState<number>(0);

  const [totalPages, setTotalPages] = useState<number>(1);
  const [page, setPage] = useState<number>(1);

  const { selectedID } = useDashboardContext();

  const insets = useSafeAreaInsets();

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <AppHeaderAction
          primary
          onPress={() => navigation.goBack()}
          icon={<Icon name="close" />}
          text="Abbrechen"
        />
      ),
    });
  }, []);

  useEffect(() => {
    const concatFilters = (appliedFilters.difficulty || '')
      .split(',')
      .filter(Boolean).length;

    const titleFilter = appliedFilters.searchTerm ? 1 : 0;
    setCountAppliedFilters(concatFilters + titleFilter);
  }, [appliedFilters]);

  const fetchAndSetTemplates = async () => {
    try {
      const { pageSize, templates, total } = await fetchTemplates({
        setType: route.params.type as 'Word' | 'Sentence',
        difficulty: difficultyFilter,
        searchTerm,
        page,
        limit: 8,
      });
      setTemplates(templates);
      setTotalPages(Math.ceil(total / pageSize));
    } catch (error) {
      logError('TemplatesSelect', error);
    }
  };

  useEffect(() => {
    fetchAndSetTemplates();
  }, [appliedFilters, page]);

  const applyFilters = () => {
    setAppliedFilters({
      difficulty: difficultyFilter,
      searchTerm,
    });
    setPage(1);
    setIsOpen(false);
    setSelectedTemplate(null);
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ y: 0, animated: true });
  }, [page]);

  const handleNextStep = () => {
    if (route.params.type === 'Word') {
      navigation.navigate('WordSet', {
        selectedID: selectedID,
        fromTemplate: selectedTemplate?.id,
        apiName: 'wordsets',
      });
      return;
    }
    navigation.navigate('SentenceSet', {
      selectedID: selectedID,
      fromTemplate: selectedTemplate?.id,
      apiName: 'sentencesets',
    });
  };

  const handleNoTemplate = () => (
    <View style={styles.noTemplate}>
      <Text
        style={{
          fontFamily: 'InterSemiBold',
          color: colors.newUI.therapist.grey60,
        }}
      >
        Keine Vorlagen gefunden
      </Text>
    </View>
  );

  return (
    <BodyWrapper
      bodyStyle={{ width: '90%' }}
      showSidebar={false}
      ref={scrollRef}
    >
      <Card
        icon="bookShelf"
        iconPosition="outside"
        title="Vorlage aus der Bibliothek auswählen"
        linkText="Speichern"
      >
        <View style={{ alignItems: 'flex-end', marginBottom: 10 }}>
          <Button
            label={
              countAppliedFilters ? `Filter (${countAppliedFilters})` : 'Filter'
            }
            look="secondary"
            onPress={() => setIsOpen(true)}
            icon="filter"
          />
        </View>
        <TemplateFilter
          isOpen={isOpen}
          onClose={() => applyFilters()}
          difficultyFilter={difficultyFilter}
          setDifficultyFilter={setDifficultyFilter}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          resetFilter={() => {
            setDifficultyFilter(undefined);
            setSearchTerm('');
          }}
        />
        {templates.length === 0 ? (
          handleNoTemplate()
        ) : (
          <KeyboardAwareScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ flexGrow: 1 }}
            enableOnAndroid={true}
            extraScrollHeight={IS_ANDROID ? insets.bottom + 150 : 0}
          >
            <View style={styles.templateCardWrapper}>
              {templates.map((template) => (
                <View
                  style={{ paddingHorizontal: 8, width: '50%' }}
                  key={template.id}
                >
                  <Template
                    key={template.id}
                    template={template}
                    onSelect={(id) => {
                      setSelectedTemplate(id);
                    }}
                    isSelected={template.id === selectedTemplate?.id}
                  />
                </View>
              ))}
            </View>
            <Pagination
              onPageChange={setPage}
              currentPage={page}
              totalPages={totalPages}
            ></Pagination>
          </KeyboardAwareScrollView>
        )}
      </Card>
      <View style={styles.templateSelectFooter}>
        <Button
          onPress={() => navigation.goBack()}
          look="tertiary"
          label="Abbrechen"
        />
        <Button
          onPress={handleNextStep}
          label="Fertigstellen"
          disabled={!selectedTemplate}
        />
      </View>
    </BodyWrapper>
  );
};
